import React from "react";
import Scaffold from "../Scaffold/Scaffold";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import { translate } from "../../translate";
import { STRINGS } from "../../strings";
import { NewPhysicianParams } from "../../messages";
import styled from "@emotion/styled";
import Flex from "../Generic/Flex";
import Question from "../OnboardingSection/Question";
import AddressDialog from "../Auth/components/AddressDialog";
import { AppButton } from "../Generic/AppButton";
import { emptyStringOrUndefinedToDefault } from "../../formatUtils";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import useOpen from "../../hooks/useOpen";

const newPhysicianSchema = z.object({
  firstName: z.string().min(2, "Must contain at least 2 characters"),
  lastName: z.string().min(2, "Must contain at least 2 characters"),
  npi: z.string().nullable().optional(),
  fax: z
    .string()
    .min(12, "Fax number must be excactly 10 digits")
    .max(14, "Fax number must be excactly 10 digits"),
  phone: z
    .string()
    .min(12, "Phone number must be excactly 10 digits")
    .max(14, "Phone number must be excactly 10 digits")
    .nullable()
    .optional(),
  address: z.string().nullable().optional(),
});

export function AddPhysicianScreen(props: {
  isLoading: boolean;
  onClickGoBack: () => void;
  onClickAddPhysician: (params: NewPhysicianParams) => void;
}) {
  const addressDialog = useOpen();

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof newPhysicianSchema>>({
    resolver: zodResolver(newPhysicianSchema),
    defaultValues: {},
  });

  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const handleClickSubmitNewPhysician = (data: z.infer<typeof newPhysicianSchema>) => {
    props.onClickAddPhysician({
      address: data.address ?? null,
      fax: data.fax,
      firstName: data.firstName,
      lastName: data.lastName,
      npi: emptyStringOrUndefinedToDefault(data.npi, null),
      phone: data.phone ?? null,
    });
  };

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.ADD_PHYSICIAN)} />}
      onClickGoBack={props.onClickGoBack}
      footer={
        <ButtonContainer>
          <AppButton
            block
            isLoading={props.isLoading}
            type="submit"
            onClick={() => buttonRef.current?.click()}
          >
            {translate(STRINGS.SUBMIT)}
          </AppButton>
        </ButtonContainer>
      }
    >
      <Form onSubmit={handleSubmit(handleClickSubmitNewPhysician)}>
        <Flex direction="column" gap={16}>
          <Flex direction="column" gap={4}>
            <LabelContainer>
              <LabelName>{translate(STRINGS.FIRST_NAME)}</LabelName>
              <RequiredFieldIndicator>*</RequiredFieldIndicator>
            </LabelContainer>
            <Question.Input
              {...register("firstName")}
              type="text"
              placeholder={translate(STRINGS.FIRST_NAME)}
            />
            {errors.firstName && <ErrorMessage>{errors.firstName.message}</ErrorMessage>}
          </Flex>
          <Flex direction="column" gap={4}>
            <LabelContainer>
              <LabelName>{translate(STRINGS.LAST_NAME)}</LabelName>
              <RequiredFieldIndicator>*</RequiredFieldIndicator>
            </LabelContainer>
            <Question.Input
              {...register("lastName")}
              type="text"
              placeholder={translate(STRINGS.LAST_NAME)}
            />
            {errors.lastName && <ErrorMessage>{errors.lastName.message}</ErrorMessage>}
          </Flex>
          <Flex direction="column" gap={4}>
            <LabelContainer>
              <LabelName>{translate(STRINGS.NPI)}</LabelName>
            </LabelContainer>
            <Question.Input {...register("npi")} type="text" placeholder={translate(STRINGS.NPI)} />
            {errors.npi && <ErrorMessage>{errors.npi.message}</ErrorMessage>}
          </Flex>
          <Flex direction="column" gap={4}>
            <LabelContainer>
              <LabelName>{translate(STRINGS.FAX_NUMBER)}</LabelName>
              <RequiredFieldIndicator>*</RequiredFieldIndicator>
            </LabelContainer>
            <Question.PhoneNumber
              {...register("fax")}
              autoFocus={false}
              control={control}
              disabled={false}
              placeholder={translate(STRINGS.FAX_NUMBER)}
            />
            {errors.fax && <ErrorMessage>{errors.fax.message}</ErrorMessage>}
          </Flex>
          <Flex direction="column" gap={4}>
            <LabelContainer>
              <LabelName>{translate(STRINGS.PHONE)}</LabelName>
            </LabelContainer>
            <Question.PhoneNumber
              {...register("phone")}
              autoFocus={false}
              control={control}
              disabled={false}
              placeholder={translate(STRINGS.PHONE)}
            />
          </Flex>
          <Flex direction="column" gap={4}>
            <LabelContainer>
              <LabelName>{translate(STRINGS.ADDRESS)}</LabelName>
            </LabelContainer>
            <Question.Input
              {...register("address")}
              placeholder={translate(STRINGS.ADDRESS)}
              onFocus={addressDialog.actions.open}
            />
            <AddressDialog
              isOpen={addressDialog.isOpen}
              onClickPlace={(place) => {
                setValue("address", place.formatted_address ?? null);
                addressDialog.actions.dismiss();
              }}
              onRequestClose={() => addressDialog.actions.dismiss()}
              defaultValue=""
              placeholder={translate(STRINGS.ADDRESS)}
            />
          </Flex>
        </Flex>
        <button hidden type="submit" ref={buttonRef} />
      </Form>
    </Scaffold>
  );
}

const ButtonContainer = styled(Flex)`
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
`;

const LabelName = styled.span`
  font-weight: bold;
`;

const LabelContainer = styled(Flex)`
  padding: 8px;
`;

const ErrorMessage = styled.span`
  color: red;
`;

const Form = styled.form`
  padding: 8px;
`;

const RequiredFieldIndicator = styled.span`
  color: red;
`;
